import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { formatTime12h } from '../utils/time';
import toast from 'react-hot-toast';

export default function LogDose({ userId, onLogged }) {
  const [meds, setMeds] = useState([]);
  const [selected, setSelected] = useState('');
  const [dosage, setDosage] = useState('');
  const [unit, setUnit] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.getMedications(userId)
      .then(data => {
        setMeds(data);
        if (data.length > 0) {
          const first = data[0];
          setSelected(first.id);
          setDosage(first.last_dosage ?? first.default_dosage ?? '');
          setUnit(first.dosage_unit ?? '');
        }
      })
      .catch(err => toast.error('Could not load medications'));
  }, [userId]);

  function handleMedChange(e) {
    const id = parseInt(e.target.value);
    setSelected(id);
    const med = meds.find(m => m.id === id);
    if (med) {
      setDosage(med.last_dosage ?? med.default_dosage ?? '');
      setUnit(med.dosage_unit ?? '');
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!selected) return;
    setLoading(true);
    try {
      await api.logDose({
        medication_id: selected,
        dosage_taken: dosage ? parseFloat(dosage) : null,
        dosage_unit: unit,
        source: 'web',
      });
      toast.success('Dose logged! ✅');
      onLogged && onLogged();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }

  const dailyMeds = meds.filter(m => m.type === 'daily');
  const otherMeds = meds.filter(m => m.type !== 'daily');

  // Group daily meds by their effective reminder time so the dropdown reads
  // "8:00 AM" / "9:00 PM" instead of one flat "Daily Medications" bucket.
  const dailyGroups = new Map();
  dailyMeds.forEach(m => {
    const key = m.effective_reminder_time || '';
    if (!dailyGroups.has(key)) dailyGroups.set(key, []);
    dailyGroups.get(key).push(m);
  });
  const sortedTimes = [...dailyGroups.keys()].sort();

  return (
    <div className="log-dose-card">
      <h2>Log a Dose</h2>
      <form onSubmit={handleSubmit}>
        <label>
          Medication
          <select value={selected} onChange={handleMedChange}>
            {sortedTimes.map(t => (
              <optgroup key={t} label={formatTime12h(t) || 'Daily Medications'}>
                {dailyGroups.get(t).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </optgroup>
            ))}
            {otherMeds.length > 0 && (
              <optgroup label="Other Medications">
                {otherMeds.map(m => <option key={m.id} value={m.id}>{m.name} ({m.type === 'interval' ? `every ${m.interval_days}d` : 'as needed'})</option>)}
              </optgroup>
            )}
          </select>
        </label>
        <div className="dosage-row">
          <label style={{flex:2}}>
            Dosage
            <input
              type="number"
              step="any"
              value={dosage}
              onChange={e => setDosage(e.target.value)}
              placeholder="Amount"
            />
          </label>
          <label style={{flex:1}}>
            Unit
            <input
              type="text"
              value={unit}
              onChange={e => setUnit(e.target.value)}
              placeholder="mg / ml"
            />
          </label>
        </div>
        <button type="submit" className="btn-primary" disabled={loading || !selected}>
          {loading ? 'Logging…' : 'Log Dose'}
        </button>
      </form>
    </div>
  );
}
